import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Reveal } from "./Reveal";
import { PhoneCall, Clock, Bot, Loader2, MessageCircle } from "lucide-react";
import { trackGA } from "@/lib/consent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Numero verde reale (dal sito dentistasostenibile.com)
export const TOLL_FREE = "800 628 192";

const CallbackForm = () => {
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("È necessario accettare l'informativa privacy.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: "Richiamata rapida",
        phone,
        message: "Richiesta di richiamata telefonica (form rapido)",
        privacy_consent: true,
        source: "richiamata_rapida",
      });
      if (window.fbq) window.fbq("track", "Lead");
      trackGA("generate_lead", { form: "richiamata_rapida" });
      setPhone("");
      setConsent(false);
      navigate("/grazie");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Si è verificato un errore. Riprova o chiama il numero verde.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-8 bg-white/10 border border-white/20 rounded-2xl p-5" data-testid="callback-form">
      <p className="text-white font-bold">Hai fretta? Ti richiamiamo noi.</p>
      <div className="mt-3 flex flex-col sm:flex-row gap-3">
        <input
          required
          type="tel"
          minLength={6}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Il tuo numero di telefono"
          className="flex-1 bg-white rounded-full px-5 py-3.5 text-base text-[#15201A] placeholder:text-[#9AA79E] focus:outline-none focus:ring-2 focus:ring-[#C98E6E]"
          data-testid="callback-phone-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="inline-flex justify-center items-center gap-2 rounded-full bg-[#C98E6E] hover:bg-[#B37A5B] disabled:opacity-60 text-white font-bold px-7 py-3.5 transition-colors duration-300 active:scale-95"
          data-testid="callback-submit-btn"
        >
          {loading && <Loader2 className="animate-spin" size={18} />}
          Richiamami
        </button>
      </div>
      <label className="mt-3 flex items-start gap-2 text-xs text-white/60 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-[#C98E6E]"
          data-testid="callback-privacy-checkbox"
        />
        <span>Acconsento al trattamento dei dati per essere ricontattato (informativa privacy). *</span>
      </label>
    </form>
  );
};

export const TollFree = () => (
  <section id="sempre-con-te" className="px-6 md:px-12 py-16 md:py-20 bg-[#385244]" data-testid="tollfree-section">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
      <Reveal>
        <h2 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">
          Sempre con <em className="italic text-[#C98E6E]">te!</em>
        </h2>
        <p className="mt-4 text-sm uppercase tracking-[0.2em] font-bold text-[#C98E6E] mb-3">Numero verde — attivo 24/7</p>
        <a
          href={`tel:${TOLL_FREE.replace(/\s/g, "")}`}
          className="font-serif-display font-light text-4xl sm:text-6xl lg:text-7xl text-white tracking-tight hover:text-[#C98E6E] transition-colors duration-300 inline-flex items-center gap-3 sm:gap-5"
          data-testid="tollfree-number"
        >
          <PhoneCall size={40} strokeWidth={1.4} className="text-[#C98E6E] shrink-0" />
          {TOLL_FREE}
        </a>
        <p className="mt-4 text-white/60 text-base">Chiamata gratuita da fisso e mobile, tutti i giorni, 24 ore su 24.</p>
        <CallbackForm />
      </Reveal>
      <Reveal delay={0.15}>
        <div className="space-y-5">
          <div className="flex items-start gap-4 bg-white/5 border border-white/15 rounded-2xl p-5">
            <Clock size={26} className="text-[#C98E6E] flex-shrink-0 mt-0.5" strokeWidth={1.6} />
            <div>
              <p className="text-white font-bold">In orari lavorativi</p>
              <p className="text-white/70 text-sm mt-1">Ti risponde <strong className="text-white">Maria Grazia</strong>, la nostra responsabile pazienti, pronta ad aiutarti e a fissare la tua visita.</p>
            </div>
          </div>
          <div className="flex items-start gap-4 bg-white/5 border border-white/15 rounded-2xl p-5">
            <Bot size={26} className="text-[#C98E6E] flex-shrink-0 mt-0.5" strokeWidth={1.6} />
            <div>
              <p className="text-white font-bold">Fuori orario e festivi</p>
              <p className="text-white/70 text-sm mt-1">Potrebbe risponderti il nostro <strong className="text-white">assistente virtuale</strong>: raccoglie la richiesta e verrai ricontattato appena possibile.</p>
            </div>
          </div>
          <div className="flex items-start gap-4 bg-white/5 border border-white/15 rounded-2xl p-5" data-testid="tollfree-clara-card">
            <MessageCircle size={26} className="text-[#C98E6E] flex-shrink-0 mt-0.5" strokeWidth={1.6} />
            <div>
              <p className="text-white font-bold">Chat e WhatsApp, sempre disponibili</p>
              <p className="text-white/70 text-sm mt-1">
                Puoi chattare con <strong className="text-white">Clara</strong>, la nostra assistente virtuale,
                24 ore su 24, oppure inviare un messaggio su <strong className="text-white">WhatsApp</strong>:
                la nostra responsabile ti risponderà il più presto possibile. O ancora, puoi chiamare subito
                il <a href="tel:3404078403" className="font-bold text-white underline decoration-[#C98E6E] hover:text-[#C98E6E] transition-colors duration-200">340 407 8403</a> e
                avrai tutte le informazioni.
              </p>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
